import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { ui } from "../lib/ui";

interface ReporteDiario {
  dia: string;
  cantidad_ventas: number;
  total: number;
  por_medio_pago: Record<string, number>;
}

interface ReporteMensual {
  year: number;
  month: number;
  cantidad_ventas: number;
  total: number;
}

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

type ReportTab = "diario" | "mensual" | "fiscal";

export default function Reportes() {
  const [tab, setTab] = useState<ReportTab>("diario");
  const today = new Date().toISOString().slice(0, 10);
  const [dia, setDia] = useState(today);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const tabs: { id: ReportTab; label: string }[] = [
    { id: "diario", label: "Diario" },
    { id: "mensual", label: "Mensual" },
    { id: "fiscal", label: "Fiscal · Monotributo" },
  ];

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Reportes</h1>
          <p className={ui.subtitle}>
            Ventas diarias, mensuales y seguimiento fiscal
          </p>
        </div>
        <div className="flex gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              className={
                tab === t.id
                  ? "bg-slate-900 text-white px-4 py-2 rounded-lg font-medium"
                  : "bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium"
              }
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className={ui.page}>
        {tab === "diario" && <Diario dia={dia} onChange={setDia} />}
        {tab === "mensual" && (
          <Mensual
            year={year}
            month={month}
            onYear={setYear}
            onMonth={setMonth}
          />
        )}
        {tab === "fiscal" && (
          <Fiscal
            year={year}
            month={month}
            onYear={setYear}
            onMonth={setMonth}
          />
        )}
      </div>
    </div>
  );
}

function Diario({
  dia,
  onChange,
}: {
  dia: string;
  onChange: (d: string) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["reporte", "diario", dia],
    queryFn: async () => {
      const { data } = await api.get<ReporteDiario>(
        "/api/reports/ventas/diario",
        { params: { dia } }
      );
      return data;
    },
  });

  const max = data
    ? Math.max(1, ...Object.values(data.por_medio_pago ?? {}))
    : 1;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end">
        <div>
          <label className={ui.label}>Día</label>
          <input
            type="date"
            className={ui.input}
            value={dia}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>

      {error && <div className={ui.error}>Error al cargar reporte.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={ui.cardPad}>
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
            Cantidad de ventas
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {isLoading ? "—" : data?.cantidad_ventas ?? 0}
          </div>
        </div>
        <div className={ui.cardPad}>
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
            Total facturado
          </div>
          <div className="text-3xl font-bold text-emerald-600">
            {isLoading ? "—" : money(data?.total ?? 0)}
          </div>
        </div>
      </div>

      <div className={ui.cardPad}>
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
          Por medio de pago
        </h3>
        {isLoading && <div className={ui.skeleton + " h-32"} />}
        {!isLoading && data &&
          (Object.keys(data.por_medio_pago ?? {}).length === 0 ? (
            <p className={ui.empty}>Sin ventas en el día.</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(data.por_medio_pago).map(([medio, monto]) => (
                <div key={medio}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700">{medio}</span>
                    <span className="font-bold text-slate-900">
                      {money(monto)}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${(monto / max) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

function Mensual({
  year,
  month,
  onYear,
  onMonth,
}: {
  year: number;
  month: number;
  onYear: (y: number) => void;
  onMonth: (m: number) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["reporte", "mensual", year, month],
    queryFn: async () => {
      const { data } = await api.get<ReporteMensual>(
        "/api/reports/ventas/mensual",
        { params: { year, month } }
      );
      return data;
    },
  });

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end">
        <div>
          <label className={ui.label}>Año</label>
          <select
            className={ui.input}
            value={year}
            onChange={(e) => onYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={ui.label}>Mes</label>
          <select
            className={ui.input}
            value={month}
            onChange={(e) => onMonth(Number(e.target.value))}
          >
            {MESES.map((m, i) => (
              <option key={i} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className={ui.error}>Error al cargar reporte.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={ui.cardPad}>
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
            Cantidad de ventas
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {isLoading ? "—" : data?.cantidad_ventas ?? 0}
          </div>
        </div>
        <div className={ui.cardPad}>
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
            Total facturado
          </div>
          <div className="text-3xl font-bold text-emerald-600">
            {isLoading ? "—" : money(data?.total ?? 0)}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FiscalData {
  year: number;
  month: number;
  mes: {
    cantidad_ventas: number;
    total: number;
    facturado: number;
    no_facturado: number;
    cant_facturadas: number;
    cant_no_facturadas: number;
  };
  anual: {
    facturado: number;
    limite_monotributo: number;
    porcentaje_consumido: number;
    restante: number;
  };
  ventas: Array<{
    id: number;
    fecha: string;
    total: number;
    tipo: string;
    factura_id: number | null;
    factura_tipo: string | null;
    factura_nro: string | null;
    factura_cae: string | null;
    factura_estado: string | null;
  }>;
}

function Fiscal({
  year,
  month,
  onYear,
  onMonth,
}: {
  year: number;
  month: number;
  onYear: (y: number) => void;
  onMonth: (m: number) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["reporte", "fiscal", year, month],
    queryFn: async () => {
      const { data } = await api.get<FiscalData>(
        "/api/reports/fiscal/mensual",
        { params: { year, month } }
      );
      return data;
    },
  });

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end">
        <div>
          <label className={ui.label}>Año</label>
          <select
            className={ui.input}
            value={year}
            onChange={(e) => onYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={ui.label}>Mes</label>
          <select
            className={ui.input}
            value={month}
            onChange={(e) => onMonth(Number(e.target.value))}
          >
            {MESES.map((m, i) => (
              <option key={i} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className={ui.error}>Error al cargar reporte fiscal.</div>}

      {isLoading || !data ? (
        <div className={ui.skeleton + " h-32"} />
      ) : (
        <>
          <MonotributoCard anual={data.anual} year={year} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={ui.cardPad}>
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                Total del mes
              </div>
              <div className="text-3xl font-bold text-slate-900">
                {money(data.mes.total)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {data.mes.cantidad_ventas} ventas
              </div>
            </div>
            <div className={ui.cardPad + " border-emerald-200 bg-emerald-50/40"}>
              <div className="text-xs text-emerald-700 uppercase tracking-wide mb-1 font-semibold">
                Facturado (AFIP)
              </div>
              <div className="text-3xl font-bold text-emerald-700">
                {money(data.mes.facturado)}
              </div>
              <div className="text-xs text-emerald-700/70 mt-1">
                {data.mes.cant_facturadas} facturas
              </div>
            </div>
            <div className={ui.cardPad + " border-amber-200 bg-amber-50/40"}>
              <div className="text-xs text-amber-700 uppercase tracking-wide mb-1 font-semibold">
                No facturado (tickets)
              </div>
              <div className="text-3xl font-bold text-amber-700">
                {money(data.mes.no_facturado)}
              </div>
              <div className="text-xs text-amber-700/70 mt-1">
                {data.mes.cant_no_facturadas} tickets
              </div>
            </div>
          </div>

          <div className={ui.cardPad}>
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
              Ventas del mes
            </h3>
            {data.ventas.length === 0 ? (
              <p className={ui.empty}>Sin ventas en el mes.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                    <tr>
                      <th className="py-2 pr-3">Fecha</th>
                      <th className="py-2 pr-3">Venta</th>
                      <th className="py-2 pr-3">Tipo</th>
                      <th className="py-2 pr-3">Comprobante</th>
                      <th className="py-2 pr-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ventas.map((v) => (
                      <tr
                        key={v.id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                          {new Date(v.fecha).toLocaleDateString("es-AR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="py-2 pr-3 font-mono text-slate-900">
                          #{v.id}
                        </td>
                        <td className="py-2 pr-3 text-slate-700">{v.tipo}</td>
                        <td className="py-2 pr-3">
                          {v.factura_id ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-semibold">
                              Factura {v.factura_tipo} {v.factura_nro}
                              {v.factura_estado === "PENDIENTE" && (
                                <span
                                  className="text-amber-600"
                                  title="Stub AFIP — sin CAE real"
                                >
                                  &#9203;
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-semibold">
                              Ticket no fiscal
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right font-bold text-slate-900 tabular-nums">
                          {money(v.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MonotributoCard({
  anual,
  year,
}: {
  anual: FiscalData["anual"];
  year: number;
}) {
  const pct = Math.min(100, anual.porcentaje_consumido);
  const color =
    pct >= 90
      ? "bg-rose-500"
      : pct >= 70
        ? "bg-amber-500"
        : "bg-emerald-500";
  const colorText =
    pct >= 90
      ? "text-rose-700"
      : pct >= 70
        ? "text-amber-700"
        : "text-emerald-700";

  return (
    <div className={ui.cardPad + " border-2 border-slate-200"}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">
            Tope monotributo {year}
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-1 tabular-nums">
            {money(anual.facturado)}
            <span className="text-base font-medium text-slate-400 ml-1">
              / {money(anual.limite_monotributo)}
            </span>
          </div>
          <div className={`text-sm font-semibold mt-1 ${colorText}`}>
            {anual.porcentaje_consumido.toFixed(1)}% consumido
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            Restante
          </div>
          <div className="text-2xl font-bold text-slate-900 tabular-nums">
            {money(Math.max(0, anual.restante))}
          </div>
        </div>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[11px] text-slate-500 mt-2">
        Solo cuenta lo facturado AFIP. Tickets no fiscales no suman al tope.
        Editá <code>monotributo_limite_anual</code> en Settings para ajustar el l&iacute;mite.
      </div>
    </div>
  );
}
